import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Link, Text } from '@hotjar/hotsauce';
import { useTargetingSettings } from '@hotjar/webapp/hooks/useTargetingSettings';
import { useCurrentSite } from '@hotjar/site';

const SettingsContent = styled.div`
  border-top: 1px dashed ${tokens.color.ui_border};
  padding: ${tokens.spacer.x3};
  color: ${tokens.color.text_default};
`;

const SettingsTitle = styled(Text)<{ showSettings: boolean }>`
  position: relative;
  padding-left: ${tokens.spacer.x3};
  cursor: pointer;
  user-select: none;

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 0;
    height: 0;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    border-style: solid;
    border-width: 5px 4px 0 4px;
    border-color: ${tokens.color.neutral_500} transparent transparent transparent;
    transform: rotate(${({ showSettings }) => (showSettings ? '0deg' : '-90deg')});
    transition-duration: 0.2s;
  }
`;

const SettingsList = styled.ul`
  margin: ${tokens.spacer.x1} 0;
`;

const SettingsListItem = styled.li`
  ${tokens.text('body2')};
`;

const LoadingDiv = styled.div`
  ${tokens.text('body2')};
  margin-left: ${tokens.spacer.x3};
`;

const SettingsEditLink = styled(Link)`
  margin-left: ${tokens.spacer.x3};
  ${tokens.text('link')};
  ${tokens.color.text_default};
`;

interface Props {
  trackClick: (elementName: string, shownTrafficCoverage?: number | null) => void;
  coveragePercentage: number;
  isCoverageMeasureComplete: boolean;
}

export const TrafficCoverageSettings = ({
  trackClick,
  coveragePercentage,
  isCoverageMeasureComplete,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const currentSite = useCurrentSite().data;
  const [showSettings, setShowSettings] = useState(false);
  const settingsLink = `/site/${currentSite?.id}/settings#targeting`;
  const {
    isLoading,
    sessionIncludeShort,
    targetingRulesPresent,
    targetingRulesWithJsTriggerPresent,
    targetingRulesWithPageRulePresent,
  } = useTargetingSettings(currentSite);

  return (
    <SettingsContent>
      <SettingsTitle
        onClick={() => {
          let elementName;
          if (isCoverageMeasureComplete && coveragePercentage === 100) {
            elementName = 'full_traffic_coverage_expand-tracking-settings';
          } else if (isCoverageMeasureComplete) {
            elementName = 'active-traffic-coverage-rate_expand-tracking-settings';
          } else {
            elementName = 'measuring_in_progress_expand-tracking-settings';
          }
          trackClick(elementName, coveragePercentage);
          setShowSettings(!showSettings);
        }}
        showSettings={showSettings}
        textType="subtitle2"
        as="span"
      >
        {t('common:sampling-widget.tracking-coverage-message.settings.title')}
      </SettingsTitle>
      {showSettings && (
        <div>
          {isLoading && <LoadingDiv>Loading ...</LoadingDiv>}
          {!isLoading && (
            <SettingsList>
              {!sessionIncludeShort && (
                <SettingsListItem>
                  {t(
                    'common:sampling-widget.tracking-coverage-message.settings.30-second-duration-disabled',
                  )}
                </SettingsListItem>
              )}
              {!targetingRulesPresent && (
                <SettingsListItem>
                  {t('common:sampling-widget.tracking-coverage-message.settings.no-targeting-rule')}
                </SettingsListItem>
              )}
              {targetingRulesWithPageRulePresent && !targetingRulesWithJsTriggerPresent && (
                <SettingsListItem>
                  {t(
                    'common:sampling-widget.tracking-coverage-message.settings.targeting-rule-with-only-page-rule',
                  )}
                </SettingsListItem>
              )}
              {!targetingRulesWithPageRulePresent && targetingRulesWithJsTriggerPresent && (
                <SettingsListItem>
                  {t(
                    'common:sampling-widget.tracking-coverage-message.settings.targeting-rule-with-only-js-trigger',
                  )}
                </SettingsListItem>
              )}
              {targetingRulesWithPageRulePresent && targetingRulesWithJsTriggerPresent && (
                <SettingsListItem>
                  {t(
                    'common:sampling-widget.tracking-coverage-message.settings.targeting-rule-with-js-trigger-and-page-rule',
                  )}
                </SettingsListItem>
              )}
            </SettingsList>
          )}
          <SettingsEditLink
            href={settingsLink}
            kind="inline"
            isExternal
            as="a"
            onClick={() => {
              let elementName;
              if (isCoverageMeasureComplete && coveragePercentage === 100) {
                elementName = 'full_traffic_coverage_edit-tracking-settings';
              } else if (isCoverageMeasureComplete) {
                elementName = 'active-traffic-coverage-rate_edit-tracking-settings';
              } else {
                elementName = 'measuring_in_progress_edit-tracking-settings';
              }
              trackClick(elementName, coveragePercentage);
            }}
          >
            {t('common:sampling-widget.tracking-coverage-message.settings.edit')}
          </SettingsEditLink>
        </div>
      )}
    </SettingsContent>
  );
};
